// src/components/BotSettings/BuyTakeProfit.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Target, AlertCircle, Plus, Trash2, ArrowUpRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export const BuyTakeProfit: React.FC<any> = ({
  masterEnabled = true,
  longDcaEnabled = false,
  onLongDcaEnabledChange = () => {},
  buyTakeProfitUseFixedAmount = false,
  onBuyTakeProfitUseFixedAmountChange = () => {},
  longDcaStages = [],
  onLongDcaStagesChange = () => {}
}) => {
  const isEffectivelyEnabled = masterEnabled && longDcaEnabled;

  const handleStageChange = (index: number, field: string, value: string) => {
    if (!masterEnabled) return;
    const nextStages = [...longDcaStages];
    nextStages[index] = { ...nextStages[index], [field]: value };
    onLongDcaStagesChange(nextStages);
  };

  const handleAddStage = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!masterEnabled || longDcaStages.length >= 5) return;
    const newStage = {
      stage: longDcaStages.length + 1,
      target_pct: '',
      target_usd: '',
      buy_pct: '',
      enabled: true
    };
    onLongDcaStagesChange([...longDcaStages, newStage]);
  };

  const handleRemoveStage = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    if (!masterEnabled) return;
    onLongDcaStagesChange(longDcaStages.filter((_, i) => i !== index).map((s, i) => ({ ...s, stage: i + 1 })));
  };

  const totalBuyPct = longDcaStages.reduce((sum: number, s: any) => sum + (Number(s.buy_pct ?? s.buyPercentage) || 0), 0);
  const isValid = longDcaStages.length === 0 || totalBuyPct <= 100;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-8">
      
      <div className={cn("bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 relative", !masterEnabled && "opacity-60")}>
        
        {!masterEnabled && (
          <div className="absolute inset-0 z-10 bg-gray-50/50 dark:bg-gray-900/60 backdrop-blur-[2px] flex items-center justify-center rounded-xl pointer-events-none">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-gray-400" />
              <div>
                <p className="font-bold text-gray-900 dark:text-white">DCA is Disabled</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Enable "Long Entry Strategy" first.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">DCA Accumulation (Long)</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Automatically accumulate more when price drops</p>
          </div>
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <ArrowUpRight className="h-6 w-6 text-blue-500" />
          </div>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border-2 border-blue-200 dark:border-blue-800 mb-6 relative z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowUpRight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <label className="text-sm font-semibold text-gray-900 dark:text-white block">Enable DCA Accumulation</label>
            </div>
            <button
              type="button"
              disabled={!masterEnabled}
              onClick={() => onLongDcaEnabledChange(!longDcaEnabled)}
              className={cn("relative inline-flex h-7 w-14 items-center rounded-full transition-colors pointer-events-auto", isEffectivelyEnabled ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600")}
            >
              <span className={cn("inline-block h-5 w-5 transform rounded-full bg-white transition-transform", isEffectivelyEnabled ? "translate-x-8" : "translate-x-1")} />
            </button>
          </div>
        </div>

        <div className={cn("space-y-6 transition-opacity duration-300", !isEffectivelyEnabled && "opacity-40 pointer-events-none grayscale")}>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Use fixed amount targets ($)</label>
              <button
                type="button"
                onClick={() => onBuyTakeProfitUseFixedAmountChange(!buyTakeProfitUseFixedAmount)}
                className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", buyTakeProfitUseFixedAmount ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700")}
              >
                <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", buyTakeProfitUseFixedAmount ? "translate-x-6" : "translate-x-1")} />
              </button>
            </div>

            {!isValid && (
              <div className="p-4 bg-red-50 text-red-800 rounded-lg text-sm flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" /> Total percentage exceeds 100%
              </div>
            )}

            <div className="space-y-4">
              {longDcaStages.map((stage: any, index: number) => {
                const targetField = buyTakeProfitUseFixedAmount ? 'target_usd' : 'target_pct';

                return (
                  <div key={index} className="p-4 rounded-lg border bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-blue-500" />
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Stage {index + 1}</h3>
                      </div>
                      <button type="button" onClick={(e) => handleRemoveStage(index, e)} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="Remove stage">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Price Drop {buyTakeProfitUseFixedAmount ? 'Amount ($)' : 'Target (%)'}</label>
                        <input type="number" step={0.01} value={stage[targetField] ?? stage.profitTargetPct ?? stage.profitAmountUsd ?? ''} onChange={(e) => handleStageChange(index, targetField, e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Buy Pct (%)</label>
                        <input type="number" min={0} max={100} step={0.1} value={stage.buy_pct ?? stage.buyPercentage ?? ''} onChange={(e) => handleStageChange(index, 'buy_pct', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {longDcaStages.length < 5 && (
              <button
                type="button"
                onClick={handleAddStage}
                className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add Stage ({longDcaStages.length}/5)</span>
              </button>
            )}
        </div>
      </div>
    </motion.div>
  );
};